import { computed, inject } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { DateUtils } from '@pc/date';
import { ButtonDateType, DepartureReturnButtonState } from '@pc/departure-return-button';
import { withDevtools } from '@pc/store';
import { CalendarErrorType, ProductInfoInterface } from '../model/calendar-restrictions.model';
import { TripDatesService } from '../services/trip-dates.service';

export interface TripDatesState {
  dates: DateRange<Date> | null | undefined;
  datesButtonState: DepartureReturnButtonState;
  datesButtonActiveState: DepartureReturnButtonState;
  dateType: ButtonDateType;
  isValid: boolean;
  calendarRestrictions: ProductInfoInterface[];
  loading: boolean;
  apiError: CalendarErrorType;
}

const initialState: TripDatesState = {
  dates: null,
  datesButtonState: DepartureReturnButtonState.Departure,
  datesButtonActiveState: DepartureReturnButtonState.Departure,
  dateType: ButtonDateType.Departure,
  isValid: true,
  calendarRestrictions: [],
  loading: false,
  apiError: CalendarErrorType.SUCCESS
};

export const TripDatesStore = signalStore(
  { providedIn: 'root' },
  withDevtools('TripDatesStore'),
  withState(initialState),
  withComputed(({ dates }, tripDatesService = inject(TripDatesService), dateUtils = inject(DateUtils)) => ({
    dateEditDetails: computed(() => {
      return tripDatesService.getDatesDetails(dates());
    }),
    tripLength: computed(() => {
      const startDate = dates()?.start;
      const endDate = dates()?.end;

      return tripDatesService.calculateTripLength(startDate, endDate);
    }),
    tripMonthsLength: computed(() => {
      const startDate = dates()?.start;
      const endDate = dates()?.end;

      return tripDatesService.calculateTripMonthsLength(startDate, endDate);
    }),
    datesISOStrings: computed(() => {
      const startDate = dates()?.start ?? null;
      const endDate = dates()?.end ?? null;
      return {
        start: dateUtils.getDateISOIgnoringTimeZone(startDate),
        end: dateUtils.getDateISOIgnoringTimeZone(endDate)
      };
    })
  })),
  withMethods((store, tripDatesService = inject(TripDatesService)) => ({
    setIsLoading(loading: boolean): void {
      patchState(store, { loading: loading });
    },
    setApiError(apiError: CalendarErrorType): void {
      patchState(store, { apiError: apiError });
    },
    setCalendarRestrictions(calendarRestrictions: ProductInfoInterface[]): void {
      patchState(store, { calendarRestrictions: calendarRestrictions });
    },
    selectDates(newDates: DateRange<Date> | null | undefined): void {
      patchState(store, { dates: newDates });
    },
    resetDates(): void {
      patchState(store, { dates: null });
    },
    clearEndDate(): void {
      const currentDates = store.dates() as DateRange<Date>;
      const updatedDates = tripDatesService.clearEndDate(currentDates);
      patchState(store, { dates: updatedDates });
    },
    clearDateRange(): void {
      const updatedDates = tripDatesService.clearDateRange();
      patchState(store, { dates: updatedDates });
    },
    setButtonState(buttonState: DepartureReturnButtonState): void {
      patchState(store, { datesButtonState: buttonState });
    },
    setButtonActiveState(activeState: DepartureReturnButtonState): void {
      patchState(store, { datesButtonActiveState: activeState });
    },
    validate(selectedDates: DateRange<Date>): void {
      const startDateLimit = tripDatesService.getStartDateLimit(store.calendarRestrictions()[0], selectedDates.start);
      const validatedState = tripDatesService.validateSelectedDates(selectedDates, startDateLimit);

      patchState(store, {
        dates: validatedState.dates ?? store.dates(),
        datesButtonState: validatedState.datesButtonState,
        datesButtonActiveState: validatedState.datesButtonActiveState,
        isValid: validatedState.isValid
      });
    },
    reset(): void {
      patchState(store, initialState);
    },
    loadCalendarRestrictions(): void {
      tripDatesService.getProductInfo().subscribe({
        next: (response) => {
          this.setIsLoading(false);
          this.setCalendarRestrictions(response.data);
          this.setApiError(
            response.data.length > 0 ? CalendarErrorType.SUCCESS : CalendarErrorType.NO_PRODUCTS_AVAILABLE
          );
        },
        error: (error) => {
          this.setApiError(CalendarErrorType.NO_PRODUCTS_AVAILABLE);
          throw error;
        }
      });
    }
  }))
);
